/**
 * Module 6C.4 Layer 1 — Upgrade modal.
 *
 * Pops on `lead_studio:upgrade_required` window event, dispatched by the
 * Axios 402 interceptor in `lib/billing.js`. Reads the structured detail
 * payload and renders tier-specific copy + upgrade-target CTAs.
 *
 * Layer 2 (Razorpay) will replace the "Get on the list" button with an
 * actual checkout link. Today the modal is informational + links to
 * Settings where the studio's current usage lives.
 */
import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { X } from "lucide-react";

import { openRazorpayCheckout, waitForActivation } from "@/lib/billing";


const COPY_BY_TYPE = {
  leads: {
    title:   "You've reached your lead limit",
    bodyFn:  (d) =>
      `${d.label} includes ${d.limit} leads ${d.window === "calendar_month" ? "per month" : "per year"}. ` +
      `You've used all ${d.used}. Upgrade to keep adding leads.`,
  },
  projects: {
    title:   "You've reached your project limit",
    bodyFn:  (d) =>
      d.window === "live"
        ? `${d.label} allows ${d.limit} active projects at a time. Complete or cancel an existing project, or upgrade to take on more concurrently.`
        : `${d.label} includes ${d.limit} projects per year. You've used all ${d.used}. Upgrade for more capacity.`,
  },
  import_locked: {
    title:   "Imports are a paid feature",
    bodyFn:  (_d) =>
      "Bulk imports from Sheets, CSV, and WedMeGood are unlocked on Studio and Pro. Free plans support manual lead entry only.",
  },
  // Module 6C.4 Layer 2.1 — happy-path variant fired from Settings /
  // sidebar / dashboard CTAs (openUpgradeProactive). Same modal, calmer
  // copy — the user chose to look, they didn't hit a wall.
  proactive: {
    title:   "Pick a plan",
    bodyFn:  (_d) =>
      "You're on the trial or Free tier. Studio and Pro unlock higher limits, bulk imports, and priority support. Cancel anytime — billing renews on the same day each cycle.",
  },
};

const TARGET_PRICING = {
  studio: {
    monthly: "₹1,999/mo · 1,000 leads + 100 projects/yr · imports unlocked",
    yearly:  "₹20,000/yr (save ~17%) · 1,000 leads + 100 projects/yr · imports unlocked",
  },
  pro: {
    monthly: "₹3,999/mo · 5,000 leads + 500 projects/yr · imports unlocked",
    yearly:  "₹40,000/yr (save ~17%) · 5,000 leads + 500 projects/yr · imports unlocked",
  },
};


export default function UpgradeModal() {
  const [detail, setDetail] = useState(null);
  const [cadence, setCadence] = useState("monthly");
  const [busyTier, setBusyTier] = useState(null);  // 'studio' | 'pro' | null
  const navigate = useNavigate();

  useEffect(() => {
    const handler = (e) => setDetail(e.detail);
    window.addEventListener("lead_studio:upgrade_required", handler);
    return () => window.removeEventListener("lead_studio:upgrade_required", handler);
  }, []);

  if (!detail) return null;

  const copy = COPY_BY_TYPE[detail.limit_type] || COPY_BY_TYPE.leads;
  const onClose = () => { if (!busyTier) { setDetail(null); } };
  const onSeeUsage = () => { setDetail(null); navigate("/settings"); };

  // Module 6C.4 Layer 2 — launch Razorpay Checkout, wait for the webhook
  // to flip plan_status to "active", then close the modal. Client never
  // decides plan state — it only watches.
  const onChooseTier = async (tier) => {
    setBusyTier(tier);
    try {
      const response = await openRazorpayCheckout({ tier, cadence });
      if (!response) {
        // User dismissed the checkout modal. No payment fired.
        setBusyTier(null);
        return;
      }
      // Payment captured client-side. Wait for the webhook to land.
      toast.message("Activating your plan…");
      const snap = await waitForActivation(tier);
      if (snap) {
        toast.success(`You're on ${snap.label}. Welcome aboard.`);
        setDetail(null);
        // Force a usage refresh in the sidebar badge.
        window.dispatchEvent(new CustomEvent("lead_studio:plan_changed", { detail: snap }));
      } else {
        toast.error("Payment captured but activation is taking longer than expected — refresh in a minute. If it still doesn't show, contact support.");
      }
    } catch (e) {
      toast.error(e?.message || "Couldn't start checkout. Try again.");
    } finally {
      setBusyTier(null);
    }
  };

  const node = (
    <>
      <div
        className="fixed inset-0 z-[70] bg-black/40"
        onClick={onClose}
        data-testid="upgrade-modal-backdrop"
      />
      <div
        className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[80] bg-white shadow-2xl"
        style={{
          width: 480, maxWidth: "92vw",
          borderTop: "3px solid var(--brand-primary)",
          border: "1px solid var(--brand-border)",
        }}
        data-testid="upgrade-modal"
        role="dialog"
        aria-labelledby="upgrade-title"
      >
        <div className="flex items-start justify-between p-6 pb-3">
          <div className="kicker" style={{ color: "var(--brand-primary)" }}>
            {detail.limit_type === "proactive" ? "CHOOSE A PLAN" : "UPGRADE REQUIRED"}
          </div>
          <button
            onClick={onClose}
            className="text-brand-muted hover:text-brand-ink"
            data-testid="upgrade-modal-close"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>
        <div className="px-6 pb-4">
          <h2 id="upgrade-title" className="font-display text-2xl mb-3" style={{ color: "var(--brand-ink)" }}>
            {copy.title}
          </h2>
          <p className="text-sm text-brand-muted leading-relaxed mb-4">
            {copy.bodyFn(detail)}
          </p>
          {/* Monthly / Yearly toggle */}
          {detail.limit_type !== "import_locked" && (detail.upgrade_to || []).length > 0 && (
            <div className="flex border border-brand-border mb-4" data-testid="cadence-toggle">
              {[
                { v: "monthly", l: "Monthly" },
                { v: "yearly",  l: "Yearly (save ~17%)" },
              ].map((o) => (
                <button
                  key={o.v}
                  onClick={() => setCadence(o.v)}
                  className={`px-3 py-2 text-xs tracking-[0.14em] uppercase flex-1 ${cadence === o.v ? "text-white" : "text-brand-muted"}`}
                  style={{ background: cadence === o.v ? "var(--brand-primary)" : "#fff" }}
                  data-testid={`cadence-${o.v}`}
                >
                  {o.l}
                </button>
              ))}
            </div>
          )}
          <div className="space-y-2">
            {(detail.upgrade_to || []).map((target) => (
              <div
                key={target}
                className="border border-brand-border p-3 flex items-start justify-between gap-3"
                data-testid={`upgrade-tier-${target}`}
              >
                <div>
                  <div className="text-sm font-medium" style={{ color: "var(--brand-ink)" }}>
                    {target === "studio" ? "Studio" : target === "pro" ? "Pro" : target}
                  </div>
                  <div className="text-xs text-brand-muted">
                    {(TARGET_PRICING[target] && TARGET_PRICING[target][cadence]) || ""}
                  </div>
                </div>
                <button
                  className="text-xs tracking-[0.14em] uppercase text-white px-3 py-1.5 shrink-0 disabled:opacity-60"
                  style={{ background: "var(--brand-primary)" }}
                  data-testid={`upgrade-cta-${target}`}
                  onClick={() => onChooseTier(target)}
                  disabled={busyTier !== null}
                >
                  {busyTier === target ? "Loading…" : `Choose ${target}`}
                </button>
              </div>
            ))}
          </div>
        </div>
        <div className="flex items-center justify-end border-t border-brand-border px-6 py-3 gap-3">
          <button
            onClick={onSeeUsage}
            className="text-xs tracking-[0.14em] uppercase text-brand-muted hover:text-brand-ink underline underline-offset-4"
            data-testid="upgrade-see-usage"
          >
            See my usage
          </button>
          <button
            onClick={onClose}
            className="text-xs tracking-[0.14em] uppercase text-brand-ink px-3 py-1.5 border border-brand-border"
            data-testid="upgrade-modal-dismiss"
          >
            Maybe later
          </button>
        </div>
      </div>
    </>
  );

  return createPortal(node, document.body);
}
